import React from 'react';
import ReactDOM from 'react-dom';
import { Provider, Store } from 'react-redux';
import _ from 'underscore';

import { StackingRootContext } from '@tableau/widgets';
import { AngularContext, AngularContextFactory } from '../factories/AngularContextFactory';
import { Module } from '../Module';

export interface ReactDirectiveProps {
  angularContext: AngularContext;
}

export interface ReactDirectiveSpec {

  /**
   * The name that the directive will be given.
   * Follows usual rules on Angular naming (e.g. 'tbAwesomeReactComponent' and 'tb:awesome-react-component' are equivalent).
   */
  name: string;

  /**
   * The react component for the component that will be instantiated by the directive.
   */
  component: React.ComponentType<any>;

  /**
   * A scope property (or array of properties) to watch instead of the `props` object itself. This should be 'props.something'.
   * See below (documentation for ReactDirectiveFactory.create) for a full explanation of how the
   * component watches this field to re-render.
   *
   * Use of this parameter is discouraged; it's preferred to re-render by assigning a new object to
   * `props`, rather than mutating individual fields and watching those separately.
   */
  watch_useIsDiscouraged?: string | string[];

  /**
   * A Redux store to which the underlying components need access. If a store is specified, the component will be
   * wrapped in a react-redux <Provider> component that gives child components access to that store.
   */
  store?: Store<any>;
}

export module ReactDirectiveFactory {

  interface ReactDirectiveScope extends ng.IScope {
    props: ReactDirectiveProps;
  }

  /**
   * Registers an element directive that instantiates a React component with that component's factory function.
   *
   * Usage:
   *
   *     ReactDirectiveFactory.create({
   *       name: 'tbAwesomeReactComponent',
   *       component: AwesomeReactComponent,
   *       watch_useIsDiscouraged: 'props.update'
   *     });
   *
   *   The above will create a directive called tbAwesomeReactComponent that can be used in an
   *   Angular template as follows:
   *
   *     <tb:awesome-react-component props="componentProps" />
   *
   *     `componentProps` is an object on the containing scope that will be passed to the React
   *     component as its props.
   *
   *
   * The component will be updated whenever the props on the scope, or the specific parameters specified
   * in the `watch` param, are updated via Angular:
   *
   *   - If no `watch` param is provided (this is the preferred usage), reassignments to the props
   *     object passed into the directive will cause the component to be rendered with those new props.
   *     - These updates generally happen inside Angular controllers and must be made using $scope.$apply
   *       (or an equivalent method that makes Angular aware of the update).
   *     - Only a new reference assignment to the full props object will be watched; assignments to
   *       individual fields are not watched. This matches React's convention of props objects being
   *       immutable.
   *
   *     For example, to update a single field on `componentProps` that will cause the
   *     <tb:awesome-react-component> to re-render, write the following code in the Angular controller:
   *
   *       $scope.$apply(() => {
   *         $scope.componentProps = {
   *           ...$scope.componentProps,
   *           someField: 'updated-value'
   *       });
   *
   *   - If a `watch` param is provided, the props object on the scope will NOT be watched as above.
   *     Instead, individual fields on the props will be watched separately.
   *
   *     For the example above, if the `watch` param is specified as `props.someField`, then any
   *     Angular update to `someField` on `$scope.componentProps` will cause the component to re-render
   *     with those new props, even if the object reference on `$scope.componentProps` has not changed:
   *
   *       // Do not mutate props objects unless you have no other choice!
   *       $scope.$apply(() => { $scope.componentProps.someField = 'updated-value' };
   *
   *     While it may be easier to code this way, use of the `watch` param is discouraged because it
   *     makes it harder to track props updates, and it goes against React's "immutable props" convention.
   */

  export function create(directiveSpec: ReactDirectiveSpec): void {
    const spec: ReactDirectiveSpec = _.extend({}, {
        watch_useIsDiscouraged: 'props'
      },
      directiveSpec);

    Module.instance().directive(spec.name, [
      'AngularContextFactory',
      (AngularContextFactory: AngularContextFactory) => {
        const stackingRoot = document.getElementById('ng-app');
        return {
          restrict: 'E',
          replace: true,
          template: '<span></span>',
          scope:  { props: '=' },
          link: ($scope: ReactDirectiveScope, element: JQuery, attrs: ng.IAttributes) => {
            const angularContext = AngularContextFactory.create($scope);
            const render = () => {
              const props: any = _.extend({ angularContext: angularContext }, $scope.props);
              const Component = spec.component;
              if (spec.store) {
                ReactDOM.render(
                  <StackingRootContext.Provider value={stackingRoot}>
                    <Provider store={spec.store}><Component {...props} /></Provider>
                  </StackingRootContext.Provider>,
                  element[0]
                );
              } else {
                ReactDOM.render(
                  <StackingRootContext.Provider value={stackingRoot}>
                    <Component {...props} />
                  </StackingRootContext.Provider>,
                  element[0]
                );
              }
            };

            if (typeof(spec.watch_useIsDiscouraged) === 'string') {
              $scope.$watch(spec.watch_useIsDiscouraged, render);
            } else {
              spec.watch_useIsDiscouraged.forEach((prop: string) => $scope.$watch(prop, render));
            }

            $scope.$on('$destroy', () => {
              ReactDOM.unmountComponentAtNode(element[0]);
            });
          }
        };
      }
    ]);
  }

}
